import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as rxjs from 'rxjs'

import * as routeBound from '../../../../route-bound'
import * as occupants from '../../occupants'
import * as records from '../../records'

import { Api } from './api.service'
import associations from './associations'
import {
  addOccupantsWithResponsiblePersons,
  reloadOnClusterAssignmentChange,
  reloadOnClusterResponsiblePersonChange,
  reloadResponsiblePersonsOnUpdate,
} from './effects'
import type { TypeParams } from './types'

// TODO: - Maybe we should move this constant to a better place
const IS_PRIMARY_ATTRIBUTE_ID = 'isPrimary'

/**
 * Responsible persons collection for fetching space record responsible persons
 */
@ngCore.Injectable()
export class Collection extends ec.Collection<TypeParams, typeof associations> {
  constructor() {
    const scenarioContext = ngCore.inject(routeBound.scenario.Context.TOKEN)
    const recordsEc = ngCore.inject(records.Collection)

    super({
      api: ngCore.inject(Api),
      associations,
      context$: rxjs
        .combineLatest([
          scenarioContext,
          recordsEc.plugins.currentEntityPlugin.feature.selectors.getId()
            .asObservable,
        ])
        .pipe(
          rxjs.map(([context, spaceRecordId]) => {
            if (!context || !ents.core.dms.data.Record.Id.isId(spaceRecordId)) {
              return undefined
            }

            return { ...context, spaceRecordId }
          }),
        ),
      identify: responsiblePerson => responsiblePerson.employee.id,
      name: ec.Name.Predefined.SpaceResponsiblePerson,
      plugins: {
        additionalFactories: [
          new ds.table.ec.Factory<TypeParams>(collection => ({
            cellConfig: ({ entity: person, attribute }) => {
              const record =
                recordsEc.plugins.currentEntityPlugin.feature.selectors
                  .getEntity()
                  .asSignal()
              // Responsible persons are managed at cluster level for clustered
              // rooms, and are locked entirely once the room's survey is
              // signed off.
              const canEditPerson =
                !record?.clusterId &&
                record?.surveyStatus !== ents.extra.space.SurveyStatus.SignedOff

              return attribute.id === IS_PRIMARY_ATTRIBUTE_ID && canEditPerson
                ? {
                    onClick: () =>
                      collection.plugins.editingPlugin.feature.actions.edit
                        .from('ResponsiblePersons.SetPrimary.Clicked')
                        .dispatch({
                          edit: {
                            input: {
                              isPrimary: !person.isPrimary,
                            },
                          },
                          id: person.employee.id,
                        }),
                  }
                : undefined
            },
            rowConfig: ({ entity: person }) => {
              const record =
                recordsEc.plugins.currentEntityPlugin.feature.selectors
                  .getEntity()
                  .asSignal()
              const canEditPerson =
                !record?.clusterId &&
                record?.surveyStatus !== ents.extra.space.SurveyStatus.SignedOff

              return {
                iconsConfigs: [
                  ...(canEditPerson
                    ? [
                        {
                          class: 'atn-icon atn-trash atn-red',
                          onClick: () => {
                            collection.plugins.editingPlugin.feature.actions.markForDeletion
                              .from('ResponsiblePersons.Remove.Clicked')
                              .dispatch({ id: person.employee.id })
                          },
                        },
                      ]
                    : []),
                ],
              }
            },
          })),
        ],
      },
    })

    /** @see {@link records.Collection} */
    const occupantsEc = ngCore.inject(occupants.recordOccupants.Collection)
    this.feature.registerEffects(
      addOccupantsWithResponsiblePersons(this, occupantsEc),
      reloadResponsiblePersonsOnUpdate(this),
      reloadOnClusterAssignmentChange(this),
      reloadOnClusterResponsiblePersonChange(this),
    )
  }
}
